import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { CaseType, MaxLength, Page, ToastType } from 'src/app/enums/enums';
import { Attorney, GetItemsParams } from 'src/app/layouts/private-layout/pages/attorney/service/attorney.model';
import { AttorneyService } from 'src/app/layouts/private-layout/pages/attorney/service/attorney.service';
import { Clinic } from 'src/app/layouts/private-layout/pages/clinic/services/clinic.model';
import { Patient } from 'src/app/layouts/private-layout/pages/patients/services/patient.model';
import { PatientService } from 'src/app/layouts/private-layout/pages/patients/services/patient.service';
import { PhonenumberPipe } from 'src/app/pipes/phonenumber.pipe';
import { ApiService } from 'src/app/services/api.service';
import { DialogueService } from 'src/app/services/dialogue.service';
import { SharedService } from 'src/app/services/shared.service';
import { ToastService } from 'src/app/services/toast.service';
import { MakeAppointmentDialogueComponent } from '../make-appointment-dialogue/make-appointment-dialogue.component';

@Component({
  selector: 'app-add-edit-patient-dialogue',
  templateUrl: './add-edit-patient-dialogue.component.html',
  styleUrls: ['./add-edit-patient-dialogue.component.scss']
})
export class AddEditPatientDialogueComponent implements OnInit {
  activeModal = inject(NgbActiveModal);
  @Input() data: any;

  public IsLoading = false;
  public IsEditPatient = false;
  patientsList: Patient[] = [];
  public TotalPatients = 0;
  PatientForm: FormGroup;
  private EditPatientID: number | null = null;
  attorneyList: Attorney[] = [];
  attorneyListGlobal: Attorney[] = [];
  currentClinic: number = 0;
  public Clinincs: Clinic[] = [];
  public SearchVal: string = '';
  public PaginationParams!: GetItemsParams;
  public MaxZipLength = MaxLength.Zip;
  public caseTypeOptions: { key: number; value: string }[] = [];
  public PatientDetails!: Patient;

  constructor(
    private patientService: PatientService,
    public sharedService: SharedService,
    private phoneNumberPipe: PhonenumberPipe,
    private toast: ToastService,
    private attorneyService: AttorneyService,
    public apiService: ApiService,
    public dialogue: DialogueService
  ) {
    this.PaginationParams = {
      searchValue: '',
      skip: 0,
      take: Number(this.sharedService.getData('records')) || Page.Size,
      sortColumn: 'firstName',
      clinicId: this.sharedService.CurrentClinic.value || 0,
      sortOrder: 'asc'
    };
    this.PatientForm = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      phoneNumber: new FormControl('', [Validators.required]),
      dateOfBirth: new FormControl(''),
      dateOfAccident: new FormControl('', [Validators.required]),
      dateOfEntry: new FormControl('', [Validators.required]),
      caseType: new FormControl('', [Validators.required]),
      attorneyId: new FormControl('', [Validators.required]),
      disabled: new FormControl(false),
      transportation: new FormControl(false), // Optional field
      oV_W: new FormControl('', [Validators.required]),
      non_Compliant: new FormControl(true),
      clinicId: new FormControl('', [Validators.required]),
      note: new FormControl(''),
      address: new FormControl(''),
      town: new FormControl(''),
      zip: new FormControl('', [Validators.pattern('^[0-9]{5}$')])
    });
    this.apiService.ClinicList.subscribe((list) => (this.Clinincs = list));
    this.attorneyService.getAttorneyByClinic(0).subscribe((result) => (this.attorneyListGlobal = result.result));
    this.caseTypeOptions = Object.keys(CaseType)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        key: Number(key),
        value: CaseType[Number(key)]
      }));
  }

  ngOnInit(): void {
    this.PatientForm.reset();
    this.IsEditPatient = this.data.isEditPatient;
    this.IsLoading = true;
    if (this.IsEditPatient) {
      const id = this.data.PatinentId;
      this.patientService.GetPatient(id).subscribe({
        next: (res: any) => {
          this.PatientDetails = { ...res.result };
          const { id, isDeleted, dischargedDate, isDischarged, ...rest } = res.result;
          this.EditPatientID = id;
          this.currentClinic = rest.clinicId;
          this.getAttorney(rest.clinicId);
          this.PatientForm.get('attorneyId')?.reset();
          this.PatientForm.setValue(rest);
          this.IsLoading = false;
        },
        complete: () => {
          this.IsLoading = false;
        }
      });
    } else {
      this.EditPatientID = null;
      this.attorneyList = [];
      this.currentClinic = this.sharedService.CurrentClinic.value || 0;
      this.PatientForm.reset();
      if (this.currentClinic) {
        this.PatientForm.get('clinicId')?.setValue(this.currentClinic);
        this.getAttorney(this.currentClinic);
      }
    }
  }

  onSelectClinic(event: any) {
    this.currentClinic = event.target.value;
    this.getAttorney(event.target.value);
  }

  getAttorney(id: number) {
    this.attorneyList = [];
    this.attorneyService.getAttorneyByClinic(id).subscribe({
      next: (result) => {
        this.attorneyList = result.result;
      },
      error: (error) => console.error(error),
      complete: () => {}
    });
  }

  AddPatient(): void {
    if (this.PatientForm.valid) {
      this.IsLoading = true;
      let data = this.PatientForm.value;
      data.phone = this.phoneNumberPipe.transform(data.phone, 'unformat');
      data.caseType = +data.caseType;
      data.attorneyId = +data.attorneyId;
      data.clinicId = +data.clinicId;
      const response = this.patientService.AddPatient(data);
      response.subscribe({
        next: (res) => {
          this.PatientForm.reset();
          this.toast.showToast({ message: `${res.result.firstName} ${res.result.lastName} added successfully.`, type: ToastType.Success });
          this.activeModal.close(res);
        },
        error: (err) => {
          this.toast.showToast({ message: err.message, type: ToastType.Error });
        },
        complete: () => {
          this.IsLoading = false;
        }
      });
    } else {
      console.log('Form is invalid');
    }
  }

  UpdatePatient() {
    if (this.PatientForm.valid) {
      this.IsLoading = true;
      let data = this.PatientForm.value;
      data.phone = this.phoneNumberPipe.transform(data.phone, 'unformat');
      data.dateOfBirth = new Date(data.dateOfBirth).toISOString();
      data.dateOfAccident = new Date(data.dateOfAccident).toISOString();
      data.dateOfEntry = new Date(data.dateOfEntry).toISOString();
      data.id = this.EditPatientID;
      data.caseType = +data.caseType;
      data.attorneyId = +data.attorneyId;
      data.clinicId = +data.clinicId;
      const response = this.patientService.UpdatePatient(data);
      response.subscribe({
        next: (res) => {
          this.PatientForm.reset();
          this.EditPatientID = null;
          this.toast.showToast({ message: `${res.result.firstName} ${res.result.lastName} updated successfully.`, type: ToastType.Success });
          this.activeModal.close(res);
        },
        error: (err) => {},
        complete: () => {
          this.IsLoading = false;
        }
      });
    } else {
      console.log('Form is invalid');
    }
  }

  DischargePatient() {
    if (this.EditPatientID) {
      this.IsLoading = true;
      this.patientService.DischargePatient(this.EditPatientID).subscribe({
        next: (res) => {
          this.toast.showToast({ message: res.message, type: ToastType.Success });
          this.activeModal.close(res);
        },
        error: (err) => {
          this.toast.showToast({ message: err.message, type: ToastType.Error });
          this.IsLoading = false;
        },
        complete: () => {
          // this.GetPatientsList(this.PaginationParams);
          this.IsLoading = false;
        }
      });
    }
  }

  ReactivePatient() {
    if (this.EditPatientID) {
      this.IsLoading = true;
      this.patientService.ReactivePatient(this.EditPatientID).subscribe({
        next: (res) => {
          this.toast.showToast({ message: res.message, type: ToastType.Success });
          this.activeModal.close(res);
        },
        error: (err) => {
          this.toast.showToast({ message: err.message, type: ToastType.Error });
          this.IsLoading = false;
        },
        complete: () => {
          // this.GetPatientsList(this.PaginationParams);
          this.IsLoading = false;
        }
      });
    }
  }

  OpenApptDialogue(patient: Patient = this.PatientDetails) {
    let data = {
      name: patient.firstName + patient.lastName,
      caseName: CaseType[patient.caseType] || '',
      da: patient.dateOfAccident,
      de: patient.dateOfEntry,
      id: patient.id,
      clinicId: patient.clinicId
    };
    this.dialogue.Open(MakeAppointmentDialogueComponent, { size: 'xl' }, data);
  }
}
